import { useCallback, useEffect, useRef, useState } from 'react'

/** Wie lange eine geklickte Vorschau höchstens stehen bleibt, falls die Daten gleich bleiben. */
const PICKED_HOLD_MS = 600

/**
 * Die **Vorschau** eines Aufklappers der Options-Leiste (Farbwähler,
 * Schriftmenü) an einem Element: der überfahrene Eintrag oder `null` (dann
 * gilt der eingestellte Wert).
 *
 * **Nach dem Klick (`'picked'`) bleibt sie stehen,** bis `settled` sich ändert
 * — der Schreibvorgang läuft über die Live-Query zurück, und ein sofortiges
 * Abräumen zeigte einen Frame lang die alte Farbe. Ändert sich nichts (die
 * gewählte war schon die eingestellte), räumt ein kurzer Zeitgeber ab.
 *
 * Ohne Typangabe hält er eine Farbe (`{ color }`); die Schrift hält
 * `{ font }`, `CanvasFlow` für die Gruppen-Leiste je Element einen Wert.
 *
 * @param settled was sich ändert, sobald der Klick gespeichert ist (Farbe, Inhalt …)
 */
export function useOptionPreview<T = { color: string | null }>(settled: unknown) {
  const [preview, setPreview] = useState<T | null>(null)
  const [held, setHeld] = useState(false)
  const heldRef = useRef(held)
  heldRef.current = held

  useEffect(() => {
    if (!heldRef.current) return
    setPreview(null)
    setHeld(false)
  }, [settled])

  useEffect(() => {
    if (!held) return
    const timer = setTimeout(() => {
      setPreview(null)
      setHeld(false)
    }, PICKED_HOLD_MS)
    return () => clearTimeout(timer)
  }, [held])

  const update = useCallback((next: T | null | 'picked') => {
    if (next === 'picked') {
      setHeld(true)
      return
    }
    setHeld(false)
    setPreview(next)
  }, [])

  return [preview, update] as const
}
